import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Check, Gift, Copy } from "lucide-react";
import { api, formatBRL } from "../lib/api";
import { toast } from "sonner";

export default function GiftCardSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [card, setCard] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled || attempts.current >= 10) {
        if (attempts.current >= 10) setStatus("timeout");
        return;
      }
      attempts.current += 1;
      try {
        const r = await api.get(`/gift-cards/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setCard(r.data.gift_card);
        } else if (r.data.status === "expired") {
          setStatus("expired");
        } else {
          setTimeout(poll, 2000);
        }
      } catch {
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  const copyCode = () => {
    if (card?.code) {
      navigator.clipboard.writeText(card.code);
      toast.success("Código copiado");
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center p-6" data-testid="giftcard-success">
      <div className="max-w-xl w-full bg-white p-10 md:p-12 text-center">
        {status === "checking" && (
          <>
            <div className="w-3 h-3 bg-[#B8A88A] rounded-full pulse-accent mx-auto mb-6"/>
            <p className="font-italic-display text-xl text-[#6B6B6B]">Confirmando seu pagamento...</p>
          </>
        )}
        {status === "paid" && card && (
          <>
            <Check size={32} className="text-[#B8A88A] mx-auto mb-4"/>
            <p className="overline text-[#B8A88A] mb-3">Pagamento confirmado</p>
            <h1 className="font-display text-3xl md:text-4xl mb-4">Seu gift card está pronto!</h1>
            <p className="text-[#6B6B6B] mb-8 text-sm">
              Enviamos uma cópia para <strong>{card.metadata?.recipient_email || "quem você escolheu"}</strong>.
            </p>
            <div className="bg-[#FAF9F7] p-6 mb-6">
              <Gift size={24} className="text-[#B8A88A] mx-auto mb-3"/>
              <p className="overline text-[#A8A8A8] mb-2">Valor</p>
              <p className="font-display text-3xl mb-4">{formatBRL(card.amount)}</p>
              <p className="overline text-[#A8A8A8] mb-2">Código</p>
              <button onClick={copyCode} className="font-mono text-lg bg-white px-4 py-2 border border-[#E8E4DF] inline-flex items-center gap-2 hover:border-[#B8A88A]" data-testid="copy-code-btn">
                {card.code} <Copy size={14}/>
              </button>
            </div>
            {card.metadata?.message && (
              <p className="font-italic-display text-base text-[#8C7B5E] mb-6 px-6">"{card.metadata.message}"</p>
            )}
            <Link to="/" className="btn-primary inline-flex">Voltar para a loja</Link>
          </>
        )}
        {status === "expired" && (
          <>
            <p className="font-display text-2xl mb-3">Sessão expirada</p>
            <p className="text-[#6B6B6B] mb-6">A sessão de pagamento expirou. Tente novamente.</p>
            <Link to="/gift-card" className="btn-primary">Tentar de novo</Link>
          </>
        )}
        {status === "timeout" && (
          <>
            <p className="font-display text-2xl mb-3">Verificando pagamento</p>
            <p className="text-[#6B6B6B] mb-6">Está demorando mais que o esperado. Se você completou o pagamento, em instantes receberá o gift card por e-mail.</p>
            <Link to="/" className="btn-primary">Voltar para a loja</Link>
          </>
        )}
        {status === "error" && (
          <>
            <p className="font-display text-2xl mb-3">Sessão inválida</p>
            <Link to="/gift-card" className="btn-primary">Voltar</Link>
          </>
        )}
      </div>
    </div>
  );
}
